"use client";

import { useEffect } from "react";
import { useInstantSearch } from "react-instantsearch";
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares";
import { useAlgoliaConsent } from "@/lib/algoliaConsent";

/**
 * Adds the InstantSearch insights middleware (`view` events, `click`
 * attribution, and queryID propagation) to the surrounding
 * `<InstantSearch>` instance — but ONLY when the visitor has granted
 * consent for the configured OneTrust category.
 *
 * Rendered as a child of `<InstantSearch>` so `useInstantSearch()`
 * resolves the right context. Returns null.
 *
 * The middleware is created with the global `search-insights` `aa`
 * function as its `insightsClient`. AlgoliaInsightsBootstrap is the
 * only thing that actually initializes that client, so even if a
 * stray middleware call were to fire ahead of consent (it shouldn't
 * because of the effect guard) it would no-op.
 */
export default function InsightsMiddleware() {
  const {
    addMiddlewares
  } = useInstantSearch();
  const {
    hasAlgoliaConsent
  } = useAlgoliaConsent();
  useEffect(() => {
    if (!hasAlgoliaConsent) return;
    let cancelled = false;
    let removeMiddleware: (() => void) | undefined;
    import("search-insights").then(({
      default: aa
    }) => {
      if (cancelled) return;
      const middleware = createInsightsMiddleware({
        insightsClient: aa
      });
      removeMiddleware = addMiddlewares(middleware);
    });
    return () => {
      cancelled = true;
      removeMiddleware?.();
    };
  }, [hasAlgoliaConsent, addMiddlewares]);
  return null;
}